import { Link } from 'react-router';
import { CircleCheck, CircleDashed, CircleX, Loader, Rocket } from 'lucide-react';
import { ROUTES } from '@/constants';
import { useDeployments, useVersionAdoption, useVersions } from '@/services/queries';
import type { Deployment } from '@/types';
import { AccentCard, type AccentTone } from '@/shared/ui/accent-card';
import { SectionHeading, JumpButton } from '@/shared/ui/section-heading';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * What is shipping right now, on the opening screen.
 *
 * ── What this shows, and what the design asks for ───────────────────────────
 *
 * The design draws a rollout as device check-ins: "612 / 900 devices, 68%,
 * pending 271, failed 17", with a big percentage and a gradient bar. We do not
 * have that. `deployments` models a rollout as a PIPELINE - queued, building,
 * signing, uploading, smoke testing, rolled out - with no per-device check-in
 * anywhere in the schema.
 *
 * So this keeps the design's shape - the big number, the bar, the three
 * counters underneath - and fills it with the pipeline. Under it sits the
 * design's real measure, now that desktops report their version when they
 * check for updates: how many machines on the build's channel are running it
 * (RolloutReach). That is check-in count, not install success, which is
 * exactly what the design's note says the number means.
 */
const STAGES = [
  'queued',
  'building',
  'signing',
  'uploading',
  'smoke_testing',
  'rolled_out',
] as const;

const STAGE_LABEL: Record<string, string> = {
  queued: 'Queued',
  building: 'Building',
  signing: 'Signing',
  uploading: 'Uploading',
  smoke_testing: 'Smoke testing',
  rolled_out: 'Rolled out',
  rolled_back: 'Rolled back',
  failed: 'Failed',
};

/** In flight means started and not finished, however it finished. */
function isActive(stage: string): boolean {
  return stage !== 'rolled_out' && stage !== 'rolled_back' && stage !== 'failed';
}

export function ActiveRollout() {
  const query = useDeployments({ limit: 5, offset: 0 });

  if (query.isPending) return <Skeleton className="h-56 w-full rounded-[14px]" />;

  const deployments = query.data?.data ?? [];
  const active = deployments.find((d) => isActive(d.stage));
  const latest = active ?? deployments[0];

  if (!latest) {
    return (
      <section>
        <SectionHeading>Active rollout</SectionHeading>
        <AccentCard icon={<Rocket size={16} strokeWidth={2} />} title="Nothing in flight">
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">
            Nothing has been deployed yet. Publishing a version and starting a rollout shows its
            progress here.
          </p>
        </AccentCard>
      </section>
    );
  }

  const reached = STAGES.indexOf(latest.stage as (typeof STAGES)[number]);
  const failed = latest.stage === 'failed' || latest.stage === 'rolled_back';
  const percent = failed ? 100 : Math.round(((reached + 1) / STAGES.length) * 100);
  const done = failed ? 0 : Math.max(0, reached);
  const remaining = failed ? 0 : STAGES.length - reached - 1;
  const tone: AccentTone = failed ? 'negative' : active ? 'brand' : 'positive';

  return (
    <section>
      <SectionHeading
        action={
          <JumpButton
            label="All rollouts"
            render={(content, className) => (
              <Link to={ROUTES.deployments} className={className}>
                {content}
              </Link>
            )}
          />
        }
      >
        {active ? 'Active rollout' : 'Last rollout'}
      </SectionHeading>

      <AccentCard
        icon={<Rocket size={16} strokeWidth={2} />}
        tone={tone}
        bar={tone}
        title={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Rollout · <span className="font-mono">{latest.environment}</span>
            </span>
            <StatusPill status={latest.stage}>
              {STAGE_LABEL[latest.stage] ?? latest.stage}
            </StatusPill>
          </div>
        }
      >
        <p className="-mt-2 mb-4 text-[11.5px] text-muted-foreground">
          Started{' '}
          {new Date(latest.startedAt).toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>

        <div className="mb-2.5 flex items-baseline gap-2">
          <span className="text-[34px] leading-none font-bold tracking-[-1px] tabular-nums">
            {percent}%
          </span>
          <span className="text-[12.5px] text-muted-foreground">
            {failed ? 'stopped' : `stage ${String(reached + 1)} of ${String(STAGES.length)}`}
          </span>
        </div>

        <div className="mb-4.5 h-2.25 overflow-hidden rounded-[5px] bg-muted">
          <div
            className={cn(
              'h-full rounded-[5px]',
              failed ? 'bg-destructive' : 'bg-linear-to-r from-brand-500 to-brand-400',
            )}
            style={{ width: `${String(percent)}%` }}
          />
        </div>

        {/*
          The design's three counters. Stages rather than devices - see the note
          at the top - and labelled so nobody reads "17" as failed machines.
        */}
        <div className="mb-4 flex gap-6">
          <div className="flex flex-1 justify-between gap-1.5">
            <span className="text-xs text-muted-foreground">Complete</span>
            <span className="text-xs font-semibold text-pill-positive-foreground">{done}</span>
          </div>
          <div className="flex flex-1 justify-between gap-1.5">
            <span className="text-xs text-muted-foreground">Remaining</span>
            <span className="text-xs font-semibold text-pill-caution-foreground">{remaining}</span>
          </div>
          <div className="flex flex-1 justify-between gap-1.5">
            <span className="text-xs text-muted-foreground">Failed</span>
            <span className="text-xs font-semibold text-pill-negative-foreground">
              {failed ? 1 : 0}
            </span>
          </div>
        </div>

        <ol className="mb-4 flex flex-wrap gap-x-4 gap-y-2">
          {STAGES.map((stage, index) => {
            const isDone = !failed && index < reached;
            const current = index === reached && !failed;
            return (
              <li
                key={stage}
                className={cn(
                  'flex items-center gap-1.5 text-xs',
                  isDone || current ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {isDone ? (
                  <CircleCheck className="size-3.5 text-pill-positive-foreground" />
                ) : current ? (
                  <Loader className="size-3.5 animate-spin text-primary" />
                ) : failed && index === reached ? (
                  <CircleX className="size-3.5 text-destructive" />
                ) : (
                  <CircleDashed className="size-3.5" />
                )}
                {STAGE_LABEL[stage]}
              </li>
            );
          })}
        </ol>

        {latest.notes && <p className="mb-4 text-[12.5px] text-muted-foreground">{latest.notes}</p>}

        <RolloutReach deployment={latest} />
      </AccentCard>
    </section>
  );
}

/** How far back a machine's check-in still counts as "on the fleet". */
const REACH_DAYS = 14;

/**
 * Machines running this build, out of the machines on its channel that checked
 * for updates in the last fortnight. Each machine counts once, at the version
 * its latest check-in reported.
 */
function RolloutReach({ deployment }: { deployment: Deployment }) {
  const versions = useVersions({ limit: 50, offset: 0 });
  const adoption = useVersionAdoption(REACH_DAYS);

  const version = versions.data?.data.find((v) => v.id === deployment.versionId);
  if (!version || !adoption.data) return null;

  // A check-in from before channels were recorded is a stable one.
  const onChannel = adoption.data.filter(
    (row) => (row.channel ?? 'stable') === version.releaseChannel,
  );
  const fleet = onChannel.reduce((sum, row) => sum + row.devices, 0);
  const reached = onChannel
    .filter((row) => row.version === version.versionNumber)
    .reduce((sum, row) => sum + row.devices, 0);
  const percent = fleet > 0 ? Math.round((reached / fleet) * 100) : 0;

  return (
    <div className="border-t border-border/60 pt-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-semibold">
          Machines on <span className="font-mono">{version.versionNumber}</span>
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {reached.toLocaleString()} / {fleet.toLocaleString()} · {percent}%
        </span>
      </div>
      <div className="mb-2 h-2.25 overflow-hidden rounded-[5px] bg-muted">
        <div
          className="h-full rounded-[5px] bg-pill-positive-foreground"
          style={{ width: `${String(percent)}%` }}
        />
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        {fleet === 0
          ? `No ${version.releaseChannel} machine has checked for updates in the last ${String(REACH_DAYS)} days.`
          : `${(fleet - reached).toLocaleString()} still on an older build. From update check-ins in the last ${String(REACH_DAYS)} days - machines check at start-up and every few hours, so this counts check-ins, not installs.`}
      </p>
    </div>
  );
}
